import { AxiosResponse } from 'axios'
import { API } from '../../common/api'
import { ignoreNotFound } from '../../common/api/errorDisposition'
import {
  azureEngineServiceUrl,
  chatServiceUrl,
  crmServiceUrl,
  engineServiceUrl,
  intelligenceServiceUrl,
  intelligenceServiceBaseUrl,
  labravAWSEngineServiceUrl,
} from '../../common/utils/constants'
import {
  AzureAccountDetails,
  AzureBackendAccountDetails,
  AzureSolution,
} from '../modules/productAccount/reducer'
import { SegmentAndGreenField } from '../../common/modules/CloudAccounts/reducer'
import { LastEvaluatedObject } from '@labrav/marketplace'
import { camelize } from 'casing'
import {
  FilterType,
  InferenceLimitUpdate,
  SellerUrlUpdate,
} from '../modules/Propensity/reducer'

const mapAzBE2FE: (
  a: AzureBackendAccountDetails
) => AzureAccountDetails = details => ({
  id: details.id,
  name: details.company_name,
  MPNID: details.mpn_id,
  industries: details.industry,
  microsoftPartnerType: details.partner_type,
  ISV: details.is_isv,
  grantConsent: true,
  terms: true,
  solutionMustBeConfiguredWithMpc: details.solution_must_be_configured_with_mpc,
  separateUserForLabraIntegrationCreatedInMpc:
    details.separate_user_for_labra_integration_created_in_mpc,
  microsoftAccountName: details.microsoft_account_name,
  reseller: details.reseller,
  hasAccessToMpcAccountAsAdminUser:
    details.has_access_to_mpc_account_as_admin_user,
  azureConfigMetadata: {
    steps: details.azure_config_metadata?.steps ?? {
      prerequisites: false,
      mpc_account_details: false,
      microsoft_product_details: false,
      link_mpc_account: false,
    },
    is_azure_onboarding_completed:
      details.azure_config_metadata?.is_azure_onboarding_completed ?? false,
  },
  azureSolutionsData: details.azure_solution_data ?? [],
  status: details.status,
})

export const fetchAzureAccountDetails = (parnterId: string) =>
  API.get(`${azureEngineServiceUrl}/partners/${parnterId}`, {
    classifyError: ignoreNotFound,
  })
    .then(({ data }) => data as AzureBackendAccountDetails)
    .then(mapAzBE2FE)

export const fetchAzureSolutionsData = async (
  partnerId: string
): Promise<AzureSolution[]> => {
  const response = await API.get<AzureSolution[]>(
    `${azureEngineServiceUrl}/partners/${partnerId}/solutions`,
    {
      classifyError: ignoreNotFound,
    }
  )
  return response.data
}

export const deactivateAzureAccount = (
  partnerId: string,
  accountId: string,
  payload: Record<string, unknown>
) => {
  return API.put(
    `${azureEngineServiceUrl}/oauth/azure/organizations/${partnerId}/accounts/${accountId}`,
    payload,
    {
      classifyError: ignoreNotFound,
    }
  )
}

export const fetchCRMConfiguration = (crmId: string) =>
  API.get(`${crmServiceUrl}/crms/${crmId}`)

export const fetchSegmentAndGreenField = (
  partnerId: string
): Promise<AxiosResponse<SegmentAndGreenField>> =>
  API.get(`${labravAWSEngineServiceUrl}/partners/${partnerId}/segment_reports`)

export const getPreSignedUrlOppSync = (
  partnerId: string,
  path: string,
  bucketName?: string
) => {
  return API.get(`${labravAWSEngineServiceUrl}/partners/${partnerId}/assets`, {
    params: {
      fields: 'aws_presigned_url',
      assets: path,
      operation_method: 'POST',
      expires_in: 3600,
      bucket_name: bucketName ? bucketName : '',
    },
  })
}

export const fetchProductsList = (partnerId: string, productType: string) => {
  return API.get(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/products`,
    {
      params: {
        product_type: productType,
      },
    }
  )
}

export type PrivateOfferSearchItem = {
  offer_id: string
  offer_name: string
  product_id: string
  account_ids: string[]
}

export const fetchPrivateOffersList = (
  partnerId: string,
  productId: string
) => {
  return API.get(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/private_offers`,
    {
      params: {
        product_id: productId,
      },
    }
  )
}

type FetchPrivateOffersSearchOptions = {
  acceptedOnly?: boolean
}

export const fetchPrivateOffersSearch = (
  partnerId: string,
  search: string,
  options?: FetchPrivateOffersSearchOptions
) => {
  return API.get<{
    items: PrivateOfferSearchItem[]
    next_token: string | null
  }>(`${labravAWSEngineServiceUrl}/partners/${partnerId}/private_offers`, {
    params: {
      search,
      max_results: 50,
      ...(options?.acceptedOnly ? { accepted_only: true } : {}),
    },
  })
}

export const fetchPrivateOffersValidation = (
  partnerId: string,
  offerId: string
) => {
  return API.get(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/private_offers/${offerId}`
  )
}

type ConnectionResponse = {
  code: number
  message: string
}

export type TestConnectionResponse = {
  aceApisConnection: ConnectionResponse
}

export const getArnConnectionStatus = async (
  partnerId: string,
  arn: string,
  environment: string
) => {
  const { data } = await API.get(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/connections`,
    {
      params: {
        iam_role: arn,
        env: environment,
      },
    }
  )
  return camelize(data) as TestConnectionResponse
}

type SolutionsAndProductsResponse = {
  cloud_config: {
    aws_solutions: Array<unknown>
    aws_products: Array<unknown>
  }
}

export const getPartnerSolutions = async (
  partnerId: string,
  options: { updateCache?: boolean } = {}
) => {
  const { updateCache = false } = options

  const { data } = await API.get(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/solutions`,
    {
      params: {
        update_cache: updateCache,
      },
    }
  )

  return data as SolutionsAndProductsResponse
}

export type AceEventReceiptValidationStatus =
  | 'in_progress'
  | 'success'
  | 'failed'
  | 'timed_out'

export type AceEventReceiptValidationResult = {
  success: boolean
  partnerId?: string
  opportunityTitle?: string
  opportunityId?: string
  createSubmitted?: boolean
  receiptFound?: boolean
  receiptPollAttempts?: number
  receiptValidationMode?: string | null
  receiptAccountId?: string | null
  receiptOpportunityId?: string | null
  receiptCreatedAt?: string | null
  errorMessage?: string | null
}

export type AceEventReceiptValidationResponse = {
  status: AceEventReceiptValidationStatus
  startedAt: string
  expiresAt?: string | null
  completedAt?: string | null
  result?: AceEventReceiptValidationResult | null
  errorMessage?: string | null
}

export type PollAceEventReceiptOptions = {
  intervalMs?: number
  maxAttempts?: number
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, ms)
    signal?.addEventListener('abort', () => {
      clearTimeout(timer)
      reject(new DOMException('Aborted', 'AbortError'))
    })
  })

/** POST to /trigger to start a new validation run. No signal — fire and forget like startRunModuleChecksApi. */
export const startAceEventReceiptValidation = async (
  partnerId: string
): Promise<{ status: 'in_progress'; startedAt: string }> => {
  const { data } = await API.post(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/sandbox/referrals/ace-event-receipt-validation/trigger`,
    {
      initial_wait_seconds: 3,
      poll_interval_seconds: 3,
      receipt_max_attempts: 12,
    }
  )
  return camelize(data) as { status: 'in_progress'; startedAt: string }
}

/** Poll GET /partners/{id} until the validation run identified by startedAt settles. */
export const pollAceEventReceiptValidationUntilSettled = async (
  partnerId: string,
  startedAt: string,
  options?: PollAceEventReceiptOptions,
  signal?: AbortSignal
): Promise<AceEventReceiptValidationResponse> => {
  const intervalMs = options?.intervalMs ?? 3000
  const maxAttempts = options?.maxAttempts ?? 60

  let lastResponse: AceEventReceiptValidationResponse = {
    status: 'timed_out',
    startedAt,
  }

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) await sleep(intervalMs, signal)

    const { data } = await API.get(
      `${engineServiceUrl}/partners/${partnerId}`,
      { params: { fetch_okta_users: false }, signal }
    )

    const raw = data?.aws_config_metadata?.ace_event_receipt_validation
    if (!raw) continue

    const field = camelize(raw) as AceEventReceiptValidationResponse

    if (field.startedAt !== startedAt) continue

    lastResponse = field

    if (
      field.status === 'success' ||
      field.status === 'failed' ||
      field.status === 'timed_out'
    ) {
      return lastResponse
    }

    if (
      field.status === 'in_progress' &&
      field.expiresAt &&
      new Date() > new Date(field.expiresAt)
    ) {
      return { ...lastResponse, status: 'timed_out' }
    }
  }

  return lastResponse
}

/** Start a new validation run (POST) then poll (GET) until settled. */
export const validateAceEventReceipt = async (
  partnerId: string,
  options?: PollAceEventReceiptOptions,
  signal?: AbortSignal
): Promise<AceEventReceiptValidationResponse> => {
  const { startedAt } = await startAceEventReceiptValidation(partnerId)
  return pollAceEventReceiptValidationUntilSettled(
    partnerId,
    startedAt,
    options,
    signal
  )
}

export const postAssetsOppSync = (
  partnerId: string,
  assets: Record<string, unknown>
) => {
  return API.post(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/assets`,
    assets
  )
}

export const calculatePropensityScore = (
  partnerId: string,
  data: { prospect: string }[],
  userId?: string,
  crmId?: string | null,
  userMail?: string | null,
  userName?: string | null,
  signalRefresh?: boolean
) => {
  return API.post(
    `${intelligenceServiceUrl}/partners/${partnerId}/calculate_ptb`,
    { data },
    {
      params: {
        user_id: userId ? userId : undefined,
        crm_id: crmId,
        user_mail: userMail,
        user_name: userName,
        signal_refresh: signalRefresh ? true : undefined,
      },
    }
  )
}

export const fetchPropensityData = (
  downloadReport?: boolean,
  partnerId?: string,
  pageSize?: number,
  lastEvaluatedObject?: LastEvaluatedObject | null,
  filters?: FilterType,
  userMails?: string[] | null,
  archived?: boolean
) => {
  return API.get(
    `${intelligenceServiceBaseUrl}/v2/partners/${partnerId}/ptb_reports`,
    {
      params: {
        download_report: downloadReport,
        page_size: pageSize,
        last_evaluated_object: lastEvaluatedObject
          ? JSON.stringify(lastEvaluatedObject)
          : null,
        ...filters,
        user_mail: userMails?.length ? userMails.join(',') : undefined,
        archived: archived,
      },
    }
  )
}

export const fetchPtbRowFeedback = (
  partnerId: string,
  userId: string,
  inferenceIds: string[]
) => {
  return API.get(
    `${intelligenceServiceUrl}/partners/${partnerId}/ptb_reports/feedback`,
    {
      params: {
        user_id: userId,
        inference_ids: inferenceIds.join(','),
      },
    }
  )
}

export const submitPtbRowFeedback = (
  partnerId: string,
  inferenceId: string,
  body: {
    vote: 'up' | 'down'
    user_id: string
    domain_name?: string
    customer_name?: string
    user_mail?: string
    user_name?: string
    comment?: string
  }
) => {
  return API.post(
    `${intelligenceServiceUrl}/partners/${partnerId}/ptb_reports/${encodeURIComponent(inferenceId)}/feedback`,
    body
  )
}

export const updatePtbRowFeedbackComment = (
  partnerId: string,
  inferenceId: string,
  body: { user_id: string; comment: string }
) => {
  return API.patch(
    `${intelligenceServiceUrl}/partners/${partnerId}/ptb_reports/${encodeURIComponent(inferenceId)}/feedback`,
    body
  )
}

export const getPreSignedUrlIntelligence = (
  partnerId: string,
  fileKeys: string[]
) => {
  return API.post(
    `${intelligenceServiceUrl}/partners/${partnerId}/generate_upload_urls`,
    {
      file_keys: fileKeys,
    }
  )
}

export const validateCsvFile = (partnerId: string, key: string) => {
  return API.post(
    `${intelligenceServiceUrl}/partners/${partnerId}/validate_csv`,
    null,
    {
      params: {
        key: key,
      },
    }
  )
}

export const postPtb = (
  partnerId: string,
  s3Key: string,
  userId?: string,
  userMail?: string,
  crmId?: string | null,
  userName?: string | null
) => {
  return API.post(
    `${intelligenceServiceUrl}/partners/${partnerId}/ptb_reports`,
    null,
    {
      params: {
        s3_key: s3Key,
        user_id: userId ? userId : undefined,
        user_mail: userMail ? userMail : undefined,
        crm_id: crmId ? crmId : undefined,
        user_name: userName,
      },
    }
  )
}

export const fetchStatus = (partnerId: string) => {
  return API.get(
    `${intelligenceServiceBaseUrl}/v2/partners/${partnerId}/get_ptb_summary`
  )
}

export const getInferenceLimit = (partnerId: string, userMails?: string[]) => {
  const url = `${intelligenceServiceUrl}/partners/${partnerId}/get_inference_limit`

  if (userMails && userMails.length > 0) {
    return API.get(url, {
      params: {
        user_mails: userMails.join(','),
      },
    })
  }

  return API.get(url)
}

export const validateDomainUrls = (
  partnerId: string,
  domainUrl: string,
  userMail: string
) => {
  return API.get(
    `${intelligenceServiceUrl}/partners/${partnerId}/validate_domain`,
    {
      params: {
        domain: domainUrl,
        user_mail: userMail,
      },
    }
  )
}

export const add_seller = (partnerId: string, data: SellerUrlUpdate) => {
  return API.put(
    `${intelligenceServiceUrl}/partners/${partnerId}/update_seller_url`,
    data
  )
}

export const checkOnboardingStatus = (partnerId: string) => {
  return API.get(
    `${intelligenceServiceUrl}/partners/${partnerId}/check_onboarded`
  )
}

export const putInferenceLimit = (
  partnerId: string,
  data: InferenceLimitUpdate
) => {
  return API.put(
    `${intelligenceServiceUrl}/partners/${partnerId}/update_inference_limit`,
    data
  )
}

export const getConversations = (partnerId: string, userId: string) => {
  return API.get(
    `${intelligenceServiceUrl}/partners/${partnerId}/smart-gtm/conversations`,
    {
      params: {
        partner_id: partnerId,
        user_id: userId,
      },
    }
  )
}

export const sendPromptToAgent = (
  partnerId: string,
  data: {
    user_id: string
    prompt: string
    session_id?: string
    conversation_title?: string
    agent_type?: string
  }
) => {
  const url = `${chatServiceUrl}/v1/partners/${partnerId}/smart-gtm/chat`
  const url2 = `${chatServiceUrl}/v1/partners/${partnerId}/chat`
  const body = {
    ...data,
    ...(data.agent_type ? {} : { partner_id: partnerId }),
  }

  return { url: data.agent_type ? url2 : url, body }
}

export const fetchConversations = async (
  partnerId: string,
  userId: string,
  page: number = 1,
  pageSize: number = 15
) => {
  const response = await API.get(
    `${intelligenceServiceUrl}/partners/${partnerId}/smart-gtm/conversations`,
    {
      params: {
        user_id: userId,
        page: page,
        page_size: pageSize,
      },
    }
  )
  return response
}

export const getChats = (
  partnerId: string,
  userId: string,
  sessionId: string,
  nextToken: string | null
) => {
  return API.get(
    `${intelligenceServiceUrl}/partners/${partnerId}/smart-gtm/conversation/history`,
    {
      params: {
        partner_id: partnerId,
        user_id: userId,
        session_id: sessionId,
        next_token: nextToken,
      },
    }
  )
}

export const getRecommendations = (partnerId: string) => {
  return API.get(
    `${intelligenceServiceUrl}/partners/${partnerId}/smart-gtm/recommendations`
  )
}

export const getCriticalActions = (partnerId: string, operationId: string) => {
  return API.get(
    `${intelligenceServiceUrl}/partners/${partnerId}/opportunities/${operationId}/critical-actions`
  )
}

export const updateActionStatus = (
  partnerId: string,
  operationId: string,
  actionId: string,
  isHidden: boolean
) => {
  return API.patch(
    `${intelligenceServiceUrl}/partners/${partnerId}/opportunities/${operationId}/critical-actions/${actionId}`,
    { is_hidden: isHidden }
  )
}

export const getRisks = (partnerId: string, opportunityId: string) => {
  return API.get(
    `${intelligenceServiceUrl}/partners/${partnerId}/opportunities/${opportunityId}/risks`
  )
}

export const postNote = (
  partnerId: string,
  opportunityId: string,
  data: { notes: string; user_id: string }
) => {
  return API.post(
    `${intelligenceServiceUrl}/partners/${partnerId}/opportunities/${opportunityId}/notes`,
    data
  )
}

export const getNotes = (partnerId: string, opportunityId: string) => {
  return API.get(
    `${intelligenceServiceUrl}/partners/${partnerId}/opportunities/${opportunityId}/notes`
  )
}

export const postLockNote = (
  partnerId: string,
  opportunityId: string,
  data: { user_id: string; lock_duration_seconds: number }
) => {
  return API.post(
    `${intelligenceServiceUrl}/partners/${partnerId}/opportunities/${opportunityId}/notes/lock`,
    data
  )
}

export const deleteLockNote = (partnerId: string, opportunityId: string) => {
  return API.delete(
    `${intelligenceServiceUrl}/partners/${partnerId}/opportunities/${opportunityId}/notes/lock`
  )
}

export const getRecordById = (partnerId: string, id: string) => {
  // v1/v2 by-id expects base64 report ids. encodeURIComponent alone breaks when
  // the Dynamo id contains "https://" — API Gateway turns %2F into path segments.
  const encodedId = encodeURIComponent(btoa(id))
  return API.get(
    `${intelligenceServiceBaseUrl}/v2/partners/${partnerId}/ptb_reports_by_id/${encodedId}`
  )
}

export const createPtbReportsExportV2 = (
  partnerId: string,
  params: Record<string, unknown>
) => {
  return API.post(
    `${intelligenceServiceBaseUrl}/v2/partners/${partnerId}/ptb_reports/exports`,
    null,
    {
      params,
    }
  )
}

export type { ReferralOppSections } from '../modules/CoSell/ReferralForm/reducer'

export const fetchAllIcps = (partnerId: string) => {
  return API.get(`${labravAWSEngineServiceUrl}/partners/${partnerId}/icps`)
}

export const updateIcpState = (
  partnerId: string,
  icpId: string,
  data: { isActive?: boolean; isDefault?: boolean }
) => {
  return API.patch(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/icp/${icpId}`,
    data
  )
}

export const getUserConfigurations = (partnerId: string, userId: string) => {
  return API.get(
    `${intelligenceServiceUrl}/partners/${partnerId}/user/${userId}`
  )
}

export const putUserConfigurations = (
  partnerId: string,
  userId: string,
  data: Record<string, unknown>
) => {
  return API.put(
    `${intelligenceServiceUrl}/partners/${partnerId}/user/${userId}`,
    data
  )
}

export const updateRecordArchivedStatus = (
  partnerId: string,
  recordIds: string[],
  archived: boolean
) => {
  return API.patch(
    `${intelligenceServiceBaseUrl}/v2/partners/${partnerId}/ptb_reports/archive`,
    {
      record_ids: recordIds,
      archived: archived,
    }
  )
}

export const retriggerPropensityCalculation = (
  partnerId: string,
  userId: string,
  crmId?: string | null,
  userMail?: string | null,
  userName?: string | null
) => {
  return API.post(
    `${intelligenceServiceUrl}/partners/${partnerId}/user/${userId}/retrigger`,
    undefined,
    {
      params: {
        crm_id: crmId,
        user_mail: userMail,
        user_name: userName,
      },
    }
  )
}
